import React, { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { MODALITY_ICONS_BY_COLUMN_ORDER } from './ModalityServiceIcons.jsx'

const ICONS = MODALITY_ICONS_BY_COLUMN_ORDER

/**
 * Shared modalities × rates column grid + footnote (Modalities & Rates page and Services & Investment).
 */
export default function ModalitiesRatesChart({ listAriaLabel }) {
  const t = useTranslation()
  const location = useLocation()
  const { language } = useLanguage()

  const slidingScalePath = language === 'pt' ? '/pt/sliding-scale' : '/sliding-scale'
  const emdrPath = language === 'pt' ? '/pt/emdr' : '/emdr'

  const rawColumns = t('modalitiesRates.columns')
  const columns = Array.isArray(rawColumns) ? rawColumns : []

  const ariaLabel = listAriaLabel || t('modalitiesRates.pageTitle')

  useEffect(() => {
    const id = location.hash.replace(/^#/, '')
    if (!id || !id.startsWith('modalities-')) return
    const timer = window.setTimeout(() => {
      const el = document.getElementById(id)
      if (el) {
        const headerOffset = 56
        const top = el.getBoundingClientRect().top + window.scrollY - headerOffset
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
      }
    }, 80)
    return () => window.clearTimeout(timer)
  }, [location.pathname, location.hash, columns.length])

  return (
    <>
      <div
        className="flex flex-col lg:flex-row lg:items-stretch rounded-xl overflow-hidden shadow-lg border border-gray-200/80"
        role="list"
        aria-label={ariaLabel}
      >
        {columns.map((col, index) => {
          const isGrey = index % 2 === 0
          const Icon = ICONS[index] || ICONS[0]
          const bgMain = isGrey ? 'bg-[#d9d9d9]' : 'bg-[#dfead8]'
          const bgPrice = isGrey ? 'bg-[#e8e8e8]' : 'bg-[#eaf3e4]'

          return (
            <section
              key={col.id || index}
              id={col.id ? `modalities-${col.id}` : undefined}
              role="listitem"
              className={`group relative flex flex-1 min-w-0 flex-col scroll-mt-[4.5rem] border-b-2 border-white lg:border-b-0 lg:border-r-2 lg:last:border-r-0 ${bgMain}`}
            >
              <div className="relative z-0 flex min-h-0 flex-1 flex-col">
                <div className="flex flex-col items-center justify-center border-b-2 border-white px-4 pb-6 pt-8 text-center">
                  <Icon />
                  <h2 className="mt-5 text-sm sm:text-base lg:text-lg font-bold text-dark-800 uppercase tracking-wide leading-snug max-w-[16rem] lg:max-w-[12.5rem] whitespace-pre-line">
                    {col.title}
                  </h2>
                </div>
                <div className="flex flex-1 flex-col items-center justify-center gap-3 border-b-2 border-white px-4 py-6">
                  <p className="w-full text-center text-sm sm:text-[0.9375rem] leading-relaxed text-dark-800/90">
                    {col.description}
                  </p>
                  {(col.id === 'slidingScale' || col.id === 'emdr') && (
                    <Link
                      to={col.id === 'slidingScale' ? slidingScalePath : emdrPath}
                      className="inline-flex items-center gap-1.5 text-xs font-normal text-dark-800/55 no-underline transition-colors hover:text-primary-darkGreen/90 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-darkGreen/50"
                    >
                      <span>{t('modalitiesRates.readMore')}</span>
                      <svg
                        className="h-3 w-3 shrink-0 opacity-65"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.15"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <path d="M2 6h6M7.25 3.75L10.25 6L7.25 8.25" />
                      </svg>
                    </Link>
                  )}
                </div>
                <div className={`px-4 py-5 ${bgPrice}`}>
                  <p className="text-center text-base sm:text-lg font-bold text-dark-800">{col.price}</p>
                </div>
              </div>
              <div
                className="pointer-events-none absolute inset-0 z-[3] bg-primary-darkGreen/0 shadow-[inset_0_0_0_0_rgba(72,96,57,0)] transition-[background-color,box-shadow] duration-200 ease-out group-hover:bg-primary-darkGreen/[0.1] group-hover:shadow-[inset_0_0_0_2px_rgba(72,96,57,0.38)]"
                aria-hidden
              />
            </section>
          )
        })}
      </div>

      <p className="mt-8 sm:mt-10 text-sm sm:text-base text-dark-800/85 text-center max-w-4xl mx-auto leading-relaxed">
        <span className="font-semibold text-dark-800">{t('modalitiesRates.footnoteLead')}:</span>{' '}
        {t('modalitiesRates.footnoteBody')}
      </p>
    </>
  )
}
